import { PropsWithChildren, useState } from "react";
import { View, StyleSheet, TouchableOpacity, StyleProp, ViewStyle } from "react-native";

import { ThemedText } from "@/components/ThemedText";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { Colors } from "@/constants/Colors";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useTheme } from "@/hooks/useTheme";

type CollapsibleProps = PropsWithChildren & {
  style?: StyleProp<ViewStyle>;
  title: string;
  badge?: string;
  menOpen?: boolean;
};

export function Collapsible({ children, style, title, badge, menOpen }: CollapsibleProps) {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const theme = useColorScheme() ?? "light";
  return (
    <View
      style={[
        styles.container,
        { borderBottomColor: colors.border },
        style,
        menOpen ? { elevation: 1, zIndex: 1 } : { elevation: -1, zIndex: -1 },
      ]}
    >
      <TouchableOpacity
        style={[styles.heading, !menOpen && { zIndex: -1 }]}
        onPress={() => setIsOpen((value) => !value)}
        activeOpacity={0.8}
      >
        <IconSymbol
          name="chevron-right"
          size={18}
          color={theme === "light" ? Colors.light.icon : Colors.dark.icon}
          style={{ transform: [{ rotate: isOpen ? "90deg" : "0deg" }] }}
        />
        <ThemedText style={{ elevation: 0 }} type="defaultSemiBold">
          {title}
        </ThemedText>
        {badge && (
          <View style={[styles.badge, { backgroundColor: colors.dangerBorder }]}>
            <ThemedText style={styles.badgeText}>{badge}</ThemedText>
          </View>
        )}
      </TouchableOpacity>
      {isOpen && <View style={styles.content}>{children}</View>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingBottom: 8,
  },
  heading: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    gap: 6,
  },
  badge: {
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 18,
  },
  content: {
    marginTop: 6,
  },
});
